import * as React from 'react';
import { Employee } from 'nomst/Stores/Decision/employee';
import { observer } from 'mobx-react';
import * as dateFns from 'date-fns';
import PayText from 'components/Common/payText';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import PayRange from 'nomst/Components/Decision/payRange';
import ConversationBadge from 'nomst/Components/Decision/conversationBadge';
import Amount from 'nomst/Components/Decision/amount';
import StockAmount from 'nomst/Components/Decision/stockAmount';
import { Amount as AmountBusinessLogic } from 'nomst/Stores/Decision/amount';
import { AmountCodeDisplayOrdinal } from 'typings/amountCode';
import { ICountryDecisionDispatcher } from 'typings/countryDecision.dispatcher';
import { IDecisionDispatcher } from 'typings/decision.dispatcher';
import { compareItem } from 'common/compareItem';
import { AmountCode } from 'typings/enums';
import './employeeCard.scss';

export interface IEmployeeCardProps {
    decisionDispatcher: Nullable<IDecisionDispatcher>;
    countryDecisionDispatcher: Nullable<ICountryDecisionDispatcher>;
    employee: Employee;
    configuration: Configuration;
    isCycleLocked: boolean;
    selectedEmployeeId: Nullable<string>;
}

export class EmployeeCard extends React.Component<IEmployeeCardProps> {
    private readonly _maximumAmountsDisplayedOnMainView: number = 4;

    constructor(props: IEmployeeCardProps) {
        super(props);
        this.stopPropagation = this.stopPropagation.bind(this);
        this.handleToggleEmployeeSelection = this.handleToggleEmployeeSelection.bind(this);
        this.handleEmployeeSelectionKeyPress = this.handleEmployeeSelectionKeyPress.bind(this);
        this.handleChangePerformanceRating = this.handleChangePerformanceRating.bind(this);
    }

    render(): React.ReactNode {
        const { employee, countryDecisionDispatcher, selectedEmployeeId } = this.props;
        const selectedClass = selectedEmployeeId === employee.employeeSourceId ? 'selected-employee' : '';
        return (
            <div className={`nomst-employee-card ${selectedClass}`}>
                <div className="flex-row">
                    <div
                        className="left-box"
                        onClick={this.handleToggleEmployeeSelection}
                        onKeyPress={this.handleEmployeeSelectionKeyPress}
                        role="button"
                        tabIndex={0}
                    >
                        <h4 className="pay-effective-date">
                            Last Pay Change {this.payEffectiveDateText}
                        </h4>
                        <h2 className="display-name">
                            {employee.displayName}
                        </h2>
                        <h3 className="job-title">{employee.jobTitleInternal}</h3>
                        <span onClick={this.stopPropagation} onKeyPress={this.stopPropagation} role="presentation">
                            <button
                                className="promotion-request pxl-link"
                                hidden={!employee.hasPromotionRequest}
                                onClick={this.handleToggleEmployeeSelection}
                            >
                                Promotion Request
                            </button>
                        </span>
                        <div className="perf-pay-range">
                            {this.renderPerformanceRating()}
                            {this.renderPayRange()}
                        </div>
                    </div>
                    <div className="right-box">
                        <div className="pay-totals">
                            {this.renderPayText(
                                'Current Pay',
                                this.currentPayText,
                                this.annualizedCurrentPayText,
                                'current-pay')}
                            {this.renderPayText(
                                'New Pay',
                                this.newPayText,
                                this.annualizedNewPayText,
                                'new-pay')}
                            {this.renderTcc()}
                            <div className="conversation-badge-container">
                                {countryDecisionDispatcher &&
                                (
                                    <ConversationBadge
                                        countryDecisionDispatcher={countryDecisionDispatcher}
                                        conversationSummary={employee.conversationSummary}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="input-groups">
                            {this.getMainViewAmounts().map(amount => this.renderAmount(amount))}
                            {this.renderStockAmount()}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderPerformanceRating(): React.ReactNode {
        const { configuration } = this.props;
        if (!configuration.ratingConfiguration || !configuration.ratingConfiguration.showPerformanceRating) {
            return null;
        }

        return (
            <div className="perf-rating-wrapper">
                <label htmlFor="employee-card__perf-rating" className="title">Perf Rating</label>
                <br />
                {this.renderPerformanceRatingText()}
            </div>
        );
    }

    private renderPerformanceRatingText(): React.ReactNode {
        const { employee, configuration, isCycleLocked } = this.props;
        if (!configuration.ratingConfiguration) {
            return null;
        }

        if (configuration.ratingConfiguration.allowEditPerformanceRating) {
            return (
                <select
                    id="employee-card__perf-rating"
                    disabled={isCycleLocked}
                    className="perf-rating-select"
                    onClick={this.stopPropagation}
                    onBlur={this.handleChangePerformanceRating}
                    onChange={this.handleChangePerformanceRating}
                    value={employee.performanceRating}
                >
                    {configuration.ratingConfiguration.performanceRatings.map((performanceRating) =>
                        (
                            <option
                                key={performanceRating.id}
                                value={performanceRating.name}
                            >
                                {performanceRating.name}
                            </option>
                        )
                    )}
                </select>
            );
        }

        const employeePerformanceRating = configuration.ratingConfiguration.getPerformanceRating(
            employee.performanceRating);

        if (!employeePerformanceRating) {
            return null;
        }

        return (
            <strong className="perf-rating">
                {employeePerformanceRating.name}
            </strong>
        );
    }

    private async handleChangePerformanceRating(
        event: React.FormEvent<HTMLSelectElement> & { target: { value: string } }): Promise<void> {
        const { employee, decisionDispatcher } = this.props;
        const initialPerformanceRating = employee.performanceRating;
        employee.changePerformanceRating(event.target.value);
        if (!decisionDispatcher) {
            return;
        }
        await decisionDispatcher.dispatchEmployeePerformanceRatingChangedAsync(
            employee.employeeSourceId,
            initialPerformanceRating,
            employee.performanceRating);
    }

    private renderPayRange(): React.ReactNode {
        const { employee, configuration } = this.props;
        if (!configuration.payRangeConfiguration.showPayRange) {
            return null;
        }

        const payRangeClass =
            configuration.ratingConfiguration && configuration.ratingConfiguration.showPerformanceRating ?
                'range-with-perf-rating' :
                'range-no-perf-rating';

        return (
            <PayRange
                payRange={employee.range}
                payAmount={employee.basePayForPayRange}
                configuration={configuration}
                className={payRangeClass}
                chartClassName="employee-card-list__pay-range"
                barHeight={5}
                pointerHeight={15}
                useCompactPayRange={true}
            />
        );
    }

    private renderAmount(amount: AmountBusinessLogic): React.ReactNode {
        const { configuration, employee, countryDecisionDispatcher, isCycleLocked } = this.props;

        const cycleBudgetField = configuration.getCycleBudgetField(amount.amountCode);
        let showMultiComponentBonus: boolean = false;
        let isLumpSumReadonly: boolean = false;
        if (amount.amountCode === AmountCode.Bonus) {
            const bonusAmount = configuration.getCycleBudgetField(AmountCode.Bonus);
            const hasTargetValue =  !!employee.plannedBonus && employee.plannedBonus > 0;
            // TODO: PSP-21488 re-implement multi component flag
            const isMultiComponent = false;
            showMultiComponentBonus = bonusAmount && hasTargetValue && isMultiComponent;
        } else if (amount.amountCode === AmountCode.LumpSum && cycleBudgetField) {
            // if override not allowed then make the amount readonly
            isLumpSumReadonly = !cycleBudgetField.rangeEnforcement.allowOverrideLumpSum;
        }

        const isAmountReadOnly = showMultiComponentBonus;
        return (
            <div key={amount.amountCode} className={`amount-container amount-container--${amount.amountCode}`}>
                <Amount
                    configuration={configuration}
                    employeeSourceId={employee.employeeSourceId}
                    amount={amount}
                    countryDecisionDispatcher={countryDecisionDispatcher}
                    title={cycleBudgetField ? cycleBudgetField.title : ''}
                    isHourly={employee.isHourly}
                    annualStandardWorkHours={employee.annualStandardWorkHours}
                    fullTimeEquivalent={employee.fullTimeEquivalent}
                    isReadOnly={isCycleLocked || isAmountReadOnly || isLumpSumReadonly}
                    idSuffix="card"
                />
            </div>
        );
    }

    private renderStockAmount(): React.ReactNode {
        const { configuration, employee, countryDecisionDispatcher, isCycleLocked } = this.props;
        const maximumAmountsDisplayedOnMainViewWithoutStock =
            this._maximumAmountsDisplayedOnMainView - 1;

        if (!employee.stockAmount ||
            employee.amounts.length > maximumAmountsDisplayedOnMainViewWithoutStock) {
            return null;
        }

        return (
            <div className="amount-container">
                <StockAmount
                    employeeSourceId={employee.employeeSourceId}
                    stockAmount={employee.stockAmount}
                    configuration={configuration}
                    countryDecisionDispatcher={countryDecisionDispatcher}
                    isReadOnly={isCycleLocked}
                />
            </div>
        );
    }

    private get payEffectiveDateText(): string {
        const { employee } = this.props;
        if (!employee.payEffectiveDate) {
            return 'N/A';
        }

        return dateFns.format(employee.payEffectiveDate, 'MMM d, yyyy');
    }

    private stopPropagation(event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>): void {
        event.stopPropagation();
    }

    private async handleToggleEmployeeSelection(): Promise<void> {
        const { countryDecisionDispatcher, employee } = this.props;
        if (!countryDecisionDispatcher) {
            return;
        }
        return countryDecisionDispatcher.dispatchEmployeeSelection(employee.employeeSourceId);
    }

    private renderPayText(
        label: string,
        payText: string,
        annualPayText: string,
        payTypeClassName: string): React.ReactNode {
        const { employee } = this.props;
        return (
            <div className={`pay-container pay-container--${payTypeClassName}`}>
                <div className="label">{label}</div>
                <PayText
                    payText={payText}
                    annualPayText={annualPayText}
                    isHourly={employee.isHourly}
                    className="pay-text"
                    payTypeClassName={payTypeClassName}
                />
            </div>
        );
    }

    private renderTcc(): React.ReactNode {
        const { configuration } = this.props;
        if (!configuration.teamPageViews.tcc) {
            return null;
        }

        return (
            <div className="pay-container pay-container--tcc">
                <div className="label">TCC</div>
                <div
                    className="pay-text"
                    data-mf-replace-inner="***"
                >
                    {this.totalCombinedCompensationText}
                </div>
            </div>
        );
    }

    private getMainViewAmounts(): AmountBusinessLogic[] {
        const { employee } = this.props;
        return employee.amounts
            .filter(amount => amount.amountCode !== AmountCode.Promotion)
            .sort((amount, nextAmount) => compareItem(
                AmountCodeDisplayOrdinal[amount.amountCode],
                AmountCodeDisplayOrdinal[nextAmount.amountCode]))
            .slice(0, this._maximumAmountsDisplayedOnMainView);
    }

    private get currentPayText(): string {
        const { employee, configuration } = this.props;
        const convertedCurrentPay = employee.isHourly ?
            employee.convertedCurrentPay :
            employee.convertedAnnualizedCurrentPay;

        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            convertedCurrentPay,
            employee.isHourly);
    }

    private get annualizedCurrentPayText(): string {
        const { employee, configuration } = this.props;
        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            employee.convertedAnnualizedCurrentPay,
            false);
    }

    private get newPayText(): string {
        const { employee, configuration } = this.props;
        const convertedNewPay = employee.isHourly ?
            employee.convertedNewPay :
            employee.convertedAnnualizedNewPay;

        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            convertedNewPay,
            employee.isHourly);
    }

    private get annualizedNewPayText(): string {
        const { employee, configuration } = this.props;
        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            employee.convertedAnnualizedNewPay,
            false);
    }

    private get totalCombinedCompensationText(): string {
        const { employee, configuration } = this.props;
        return configuration.format.formatCurrencyValue(
            employee.currentIsoCurrencyCode,
            employee.convertedTotalCombinedCompensation,
            false);
    }

    private handleEmployeeSelectionKeyPress(e: React.KeyboardEvent<HTMLDivElement>): void {
        if (e.key === 'Enter') {
            this.handleToggleEmployeeSelection();
        }
    }
}

export default observer(EmployeeCard);